import { useCallback, useState } from 'react';

import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { log } from '../../loggers/Logger';

export interface ReplyViewModelResult {
  readonly replyText: string;
  readonly setReplyText: (value: string) => void;
  readonly submitReply: () => void;
}

/** Presentation logic for composing and submitting a reply to a comment thread. */
export function useReplyViewModel(threadId: string): ReplyViewModelResult {
  const manager = useInlineCommentManager();

  const [replyText, setReplyText] = useState('');

  // When the user submits a reply, create the comment and clear the input.
  const submitReply = useCallback(() => {
    if (!replyText.trim()) return;
    const body = replyText.trim();
    setReplyText('');
    manager.createComment({ kind: 'reply', threadId, body }).catch((err) => {
      log.error({ err, threadId }, 'Failed to add reply');
      // Restore the draft and surface the failure — the input has no inline alert slot.
      setReplyText(body);
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred. Please report a bug.';
      FlashbarMessageBroker.create().pushErrorMessage(message);
    });
  }, [replyText, manager, threadId]);

  return {
    replyText,
    setReplyText,
    submitReply,
  };
}
